import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Animated,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { TickerSuggestion } from '@/services/chat/tickerAutocompleteService';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');
const MAX_HEIGHT = 280; // Maximum height for the autocomplete list

interface TickerAutocompleteProps {
  visible: boolean;
  suggestions: TickerSuggestion[];
  onSelect: (ticker: string) => void;
}

export const TickerAutocomplete: React.FC<TickerAutocompleteProps> = ({
  visible,
  suggestions,
  onSelect,
}) => {
  const [animatedHeight] = React.useState(new Animated.Value(0));

  React.useEffect(() => {
    if (visible && suggestions.length > 0) {
      console.log(
        `💰 [TickerAutocomplete] Showing autocomplete with ${suggestions.length} suggestions`
      );
      Animated.spring(animatedHeight, {
        toValue: 1,
        useNativeDriver: false,
        tension: 80,
        friction: 10,
      }).start();
    } else {
      Animated.timing(animatedHeight, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false,
      }).start();
    }
  }, [visible, suggestions.length]);

  if (!visible || suggestions.length === 0) {
    return null;
  }

  // Calculate dynamic height based on number of items
  const itemHeight = 70;
  const headerHeight = 40;
  const listHeight = Math.min(suggestions.length * itemHeight, MAX_HEIGHT - headerHeight);
  const totalHeight = listHeight + headerHeight;

  const animatedStyle = {
    maxHeight: animatedHeight.interpolate({
      inputRange: [0, 1],
      outputRange: [0, totalHeight],
    }),
    opacity: animatedHeight,
  };

  const renderTickerItem = ({ item, index }: { item: TickerSuggestion; index: number }) => {
    return (
      <TouchableOpacity
        style={[styles.tickerItem, index === suggestions.length - 1 && styles.lastTickerItem]}
        onPress={() => onSelect(item.ticker)}
        activeOpacity={0.6}
      >
        {/* Icon */}
        <View style={styles.iconContainer}>
          <View style={styles.icon}>
            <Ionicons name="trending-up" size={20} color={Colors.accent.primary} />
          </View>
        </View>

        {/* Ticker Info */}
        <View style={styles.tickerInfo}>
          <View style={styles.tickerRow}>
            <Text style={styles.tickerSymbol} numberOfLines={1}>
              ${item.ticker}
            </Text>
            {item.exchange && (
              <View style={styles.exchangeBadge}>
                <Text style={styles.exchangeText}>{item.exchange}</Text>
              </View>
            )}
          </View>

          <Text style={styles.companyName} numberOfLines={1}>
            {item.name}
          </Text>
        </View>

        {/* Dollar Icon */}
        <View style={styles.dollarIcon}>
          <Text style={styles.dollarSymbol}>$</Text>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <Animated.View style={[styles.container, animatedStyle]}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.dollarSymbolHeader}>$</Text>
        <Text style={styles.headerText}>
          {suggestions.length === 1 ? '1 stock' : `${suggestions.length} stocks`}
        </Text>
      </View>

      {/* Tickers List */}
      <FlatList
        data={suggestions}
        keyExtractor={item => item.ticker}
        renderItem={renderTickerItem}
        style={styles.list}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        bounces={false}
        removeClippedSubviews={true}
        maxToRenderPerBatch={10}
        initialNumToRender={5}
        windowSize={3}
      />
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: Colors.background.secondary,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 10,
    zIndex: 1000,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: `${Colors.accent.primary}10`,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
    gap: 8,
  },
  dollarSymbolHeader: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.accent.primary,
  },
  headerText: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.accent.primary,
  },
  list: {
    flex: 1,
  },
  tickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: Colors.background.secondary,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: Colors.border.subtle,
    gap: 12,
  },
  lastTickerItem: {
    borderBottomWidth: 0,
  },
  iconContainer: {
    width: 44,
    height: 44,
  },
  icon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: `${Colors.accent.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tickerInfo: {
    flex: 1,
    gap: 4,
  },
  tickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  tickerSymbol: {
    fontSize: 18,
    fontWeight: '700',
    color: Colors.text.primary,
    letterSpacing: 0.5,
  },
  exchangeBadge: {
    backgroundColor: `${Colors.accent.primary}20`,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  exchangeText: {
    fontSize: 10,
    fontWeight: '600',
    color: Colors.accent.primary,
    letterSpacing: 0.5,
  },
  companyName: {
    fontSize: 13,
    fontWeight: '500',
    color: Colors.text.secondary,
  },
  dollarIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: `${Colors.accent.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dollarSymbol: {
    fontSize: 18,
    fontWeight: '700',
    color: Colors.accent.primary,
  },
});
